import { useTranslation } from '@jq4race/i18n';
import type { PhotoAngle } from '@jq4race/shared';
import { PHOTO_ANGLES } from '@jq4race/shared';
import { useEffect } from 'react';

import type { SlotStatus } from './PhotoCaptureSlot';
import { PhotoCaptureSlot } from './PhotoCaptureSlot';

export interface SlotState {
  status: SlotStatus;
  key?: string;
  thumbnailUrl?: string;
  errorMessage?: string;
  blob?: Blob;
}

export interface PhotoCaptureGridProps {
  slots: Record<PhotoAngle, SlotState>;
  onSlotCapture: (angle: PhotoAngle) => void;
  onSlotRetake: (angle: PhotoAngle) => void;
  /** Called whenever the allDone status changes */
  onAllDoneChange: (allDone: boolean) => void;
}

const ANGLE_LABEL_KEYS: Record<PhotoAngle, string> = {
  front: 'photoAngles.front',
  back: 'photoAngles.back',
  driver: 'photoAngles.driver',
  passenger: 'photoAngles.passenger',
};

/**
 * Renders 4 PhotoCaptureSlot components in a 2×2 grid.
 * Tracks overall readiness (all 4 uploaded) and surfaces it via onAllDoneChange.
 */
export function PhotoCaptureGrid({
  slots,
  onSlotCapture,
  onSlotRetake,
  onAllDoneChange,
}: PhotoCaptureGridProps) {
  const { t } = useTranslation('checkin');
  const allDone = PHOTO_ANGLES.every((angle) => slots[angle].status === 'done');

  useEffect(() => {
    onAllDoneChange(allDone);
  }, [allDone, onAllDoneChange]);

  return (
    <fieldset className="grid grid-cols-2 gap-3 border-0 p-0 m-0">
      {PHOTO_ANGLES.map((angle) => {
        const slot = slots[angle];
        return (
          <PhotoCaptureSlot
            key={angle}
            angle={angle}
            label={t(ANGLE_LABEL_KEYS[angle])}
            status={slot.status}
            thumbnailUrl={slot.thumbnailUrl}
            errorMessage={slot.errorMessage}
            onCapture={() => onSlotCapture(angle)}
            onRetake={() => onSlotRetake(angle)}
          />
        );
      })}
    </fieldset>
  );
}
